import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { Clipboard, Download, ExternalLink, Loader2, Mail, MessageCircle, Star, X } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";

const cleanName = (customer = {}) => customer.kunde_name || customer.kunde_firma || "";

export default function ReviewRequestForCustomer({ customer, onClose, onPrepareEmail, onAddNote }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const qrRef = useRef(null);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/google-review-link`)
      .then(({ data }) => { if (active) setRecord(data.record || null); })
      .catch((err) => { if (active) setError(err.response?.data?.detail || "Der Google-Bewertungslink konnte nicht geladen werden."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const customerLabel = customer?.kunde_firma || customer?.kunde_name || "diesem Kunden";
  const name = cleanName(customer);
  const reviewUrl = record?.review_url || "";
  const whatsappText = useMemo(() => `Hallo${name ? ` ${name}` : ""},\n\nvielen Dank für die Zusammenarbeit. Wenn du mit unserer Leistung zufrieden warst, würden wir uns sehr über eine kurze Google-Bewertung freuen. Das hilft unserem Unternehmen sehr:\n\n${reviewUrl}\n\nVielen Dank für deine Zeit!`, [name, reviewUrl]);
  const emailSubject = "Vielen Dank – deine Google-Bewertung hilft uns sehr";
  const emailBody = useMemo(() => `Hallo${name ? ` ${name}` : ""},\n\nvielen Dank für dein Vertrauen und die Zusammenarbeit. Wenn du mit unserer Leistung zufrieden warst, freuen wir uns sehr über eine kurze Google-Bewertung. Damit hilfst du anderen Kundinnen und Kunden bei der Orientierung:\n\n${reviewUrl}\n\nVielen Dank für deine Zeit und herzliche Grüße`, [name, reviewUrl]);

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setNotice(`${label} wurde kopiert.`);
    } catch {
      setError("Kopieren war nicht möglich. Bitte kopiere den Text manuell.");
    }
  };

  const openWhatsapp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(whatsappText)}`, "_blank", "noopener,noreferrer");
    setNotice("WhatsApp wurde mit einem vorbereiteten Text geöffnet. Bitte Empfänger und Nachricht vor dem Senden prüfen.");
  };

  const prepareEmail = () => {
    if (!customer?.kunde_email) {
      setError("Für diesen Kunden ist keine E-Mail-Adresse hinterlegt.");
      return;
    }
    onPrepareEmail?.({ subject: emailSubject, body: emailBody });
    onClose?.();
  };

  const addLog = async (channel) => {
    try {
      await onAddNote?.(`Google-Bewertungsanfrage am ${new Date().toLocaleDateString("de-DE")} über ${channel} vorbereitet.`);
      setNotice("Die Vorbereitung wurde als Notiz in der Kundenakte festgehalten.");
    } catch {
      setError("Die Notiz konnte nicht gespeichert werden.");
    }
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const anchor = document.createElement("a");
    anchor.href = canvas.toDataURL("image/png");
    anchor.download = "google-bewertung-qr-code.png";
    anchor.click();
    setNotice("QR-Code wurde als PNG heruntergeladen.");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="crm-review-request-modal">
      <Card className="w-full max-w-2xl bg-white p-5 max-h-[90vh] overflow-y-auto" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2"><Star className="w-5 h-5 text-[var(--brand-accent)]" /><h3 className="text-lg font-semibold text-[var(--brand-ink)]">Google-Bewertung anfragen</h3></div>
            <p className="mt-1 text-sm text-[var(--brand-muted)]">Für: <span className="font-medium text-[var(--brand-ink)]">{customerLabel}</span></p>
          </div>
          <button onClick={onClose} className="p-1" aria-label="Schließen"><X className="w-5 h-5 text-[var(--brand-muted)]" /></button>
        </div>

        {notice && <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{notice}</div>}
        {error && <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-[var(--brand-muted)]"><Loader2 className="w-4 h-4 animate-spin" /> Google-Bewertungslink wird geladen …</div>
        ) : !record ? (
          <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
            Für diese Organisation ist noch kein Google-Bewertungslink eingerichtet. Richte ihn zuerst unter <strong>Marketing → Google Bewertungen</strong> ein. Danach ist die Bewertungsanfrage direkt für jeden bestehenden CRM-Kunden verfügbar.
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="rounded-lg bg-[var(--brand-soft)] p-4">
              <p className="font-semibold text-[var(--brand-ink)]">Direkter Bewertungslink</p>
              <div className="mt-2 flex items-center gap-2"><input value={reviewUrl} readOnly className="min-w-0 flex-1 rounded-md border border-[var(--brand-border)] bg-white px-3 py-2 text-xs text-[var(--brand-muted)]" /><Button type="button" variant="outline" size="sm" onClick={() => copy(reviewUrl, "Link")}><Clipboard className="w-4 h-4" /></Button><a href={reviewUrl} target="_blank" rel="noreferrer" className="text-[var(--brand-primary)]"><ExternalLink className="w-4 h-4" /></a></div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <button type="button" onClick={openWhatsapp} className="rounded-lg border border-[var(--brand-border)] p-4 text-left hover:border-[var(--brand-primary)]" data-testid="crm-review-whatsapp-btn"><MessageCircle className="w-5 h-5 text-[var(--brand-accent)]" /><p className="mt-2 font-semibold text-[var(--brand-ink)]">WhatsApp vorbereiten</p><p className="mt-1 text-xs leading-5 text-[var(--brand-muted)]">Öffnet WhatsApp mit persönlichem Text und Link. Du wählst den Empfänger und sendest selbst.</p></button>
              <button type="button" onClick={prepareEmail} disabled={!customer?.kunde_email} className="rounded-lg border border-[var(--brand-border)] p-4 text-left hover:border-[var(--brand-primary)] disabled:cursor-not-allowed disabled:opacity-50" data-testid="crm-review-email-btn"><Mail className="w-5 h-5 text-[var(--brand-accent)]" /><p className="mt-2 font-semibold text-[var(--brand-ink)]">E-Mail vorbereiten</p><p className="mt-1 text-xs leading-5 text-[var(--brand-muted)]">Öffnet den vorhandenen E-Mail-Editor. Der Versand erfolgt nur nach deiner Bestätigung.</p></button>
            </div>

            <div className="grid gap-4 sm:grid-cols-[180px_1fr] rounded-lg border border-[var(--brand-border)] p-4">
              <div className="text-center"><div ref={qrRef} className="inline-block bg-white p-2"><QRCodeCanvas value={reviewUrl} size={150} level="M" includeMargin /></div><Button type="button" variant="outline" size="sm" className="mt-2" onClick={downloadQr}><Download className="w-4 h-4 mr-1" /> QR-Code als PNG</Button></div>
              <div><p className="font-semibold text-[var(--brand-ink)]">Für Termin, Rechnung oder NFC</p><p className="mt-1 text-sm leading-6 text-[var(--brand-muted)]">Der QR-Code und der direkte Link stammen aus deinem zentral gespeicherten Google-Unternehmensprofil. Du kannst ihn ausdrucken, dem Kunden zeigen oder den Link auf einem NFC-Tag speichern.</p><div className="mt-3 flex flex-wrap gap-2"><Button type="button" variant="outline" size="sm" onClick={() => copy(whatsappText, "WhatsApp-Text")}>WhatsApp-Text kopieren</Button><Button type="button" variant="outline" size="sm" onClick={() => addLog("CRM")}>Als Notiz festhalten</Button></div></div>
            </div>

            <p className="text-xs leading-5 text-[var(--brand-muted)]">Es werden keine Google-Bewertungen, Sterne oder Klicks verfolgt. Eine CRM-Notiz wird nur erstellt, wenn du „Als Notiz festhalten“ aktiv auswählst.</p>
          </div>
        )}
      </Card>
    </div>
  );
}
